import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, CheckCircle, Info } from 'lucide-react';
import { useCookiePreferences, CookiePreferences } from '../hooks/useCookiePreferences';

const CookieBanner: React.FC = () => {
  const { preferences, isAccepted, savePreferences, acceptAll, acceptNecessary } = useCookiePreferences();
  const [showBanner, setShowBanner] = useState(() => {
    // Check localStorage immediately on mount
    if (typeof window !== 'undefined') {
      const accepted = localStorage.getItem('cookiesAccepted');
      return !accepted; // Show banner if not accepted
    }
    return true; // Default to showing banner
  });
  const [showSettings, setShowSettings] = useState(false);

  // Update banner visibility when isAccepted changes
  React.useEffect(() => {
    if (isAccepted) {
      setShowBanner(false);
    } else {
      // Double check localStorage in case hook hasn't updated yet
      if (typeof window !== 'undefined') {
        const accepted = localStorage.getItem('cookiesAccepted');
        setShowBanner(!accepted);
      }
    }
  }, [isAccepted]);

  const handleAcceptAll = () => {
    acceptAll();
    setShowBanner(false);
  };

  const handleAcceptNecessary = () => {
    acceptNecessary();
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (key: keyof CookiePreferences) => {
    if (key === 'necessary') return; // No se puede desactivar
    const newPreferences = {
      ...preferences,
      [key]: !preferences[key]
    };
    savePreferences(newPreferences);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl"
        >
          <div className="container-custom py-6">
            {!showSettings ? (
              // Banner principal
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Info className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Utilizamos cookies para mejorar su experiencia
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        Utilizamos cookies y tecnologías similares para proporcionar, proteger y mejorar nuestros servicios. 
                        Al hacer clic en "Aceptar todas", usted consiente el uso de todas las cookies. 
                        Para obtener más información sobre cómo utilizamos las cookies, consulte nuestra{' '}
                        <a 
                          href="/cookies" 
                          className="text-primary-600 hover:text-primary-800 underline font-medium"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          política de cookies
                        </a>{' '}
                        y nuestra{' '}
                        <a 
                          href="/privacidad" 
                          className="text-primary-600 hover:text-primary-800 underline font-medium"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          política de privacidad
                        </a>.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="flex items-center justify-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    <Settings className="w-4 h-4" />
                    Configurar
                  </button>
                  <button
                    onClick={handleAcceptNecessary}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Solo necesarias
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Aceptar todas
                  </button>
                </div>

                <button
                  onClick={() => setShowBanner(false)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Cerrar banner de cookies"
                  aria-label="Cerrar banner de cookies"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              // Panel de configuración
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Configuración de cookies
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    title="Cerrar configuración"
                    aria-label="Cerrar configuración de cookies"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Cookies necesarias */}
                  <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900">Cookies necesarias</h4>
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Siempre activas
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar.
                      </p>
                    </div>
                    <div className="flex items-center ml-4">
                                             <input
                         type="checkbox"
                         checked={preferences.necessary}
                         disabled
                         className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                         aria-label="Cookies necesarias (siempre activas)"
                       />
                    </div>
                  </div>

                  {/* Cookies analíticas */}
                  <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Cookies analíticas</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Nos ayudan a entender cómo interactúan los visitantes con el sitio web recopilando y reportando información de forma anónima.
                      </p>
                      <p className="text-xs text-gray-500">
                        Incluye Google Analytics y herramientas similares.
                      </p>
                    </div>
                    <div className="flex items-center ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={() => handlePreferenceChange('analytics')}
                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {/* Cookies de marketing */}
                  <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Cookies de marketing</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Se utilizan para rastrear a los visitantes en los sitios web con el fin de mostrar anuncios relevantes y atractivos.
                      </p>
                      <p className="text-xs text-gray-500">
                        Incluye cookies de redes sociales y publicidad.
                      </p>
                    </div>
                    <div className="flex items-center ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.marketing}
                        onChange={() => handlePreferenceChange('marketing')}
                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {/* Cookies de preferencias */}
                  <div className="flex items-start justify-between p-4 bg-white border border-gray-200 rounded-lg">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">Cookies de preferencias</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Permiten que el sitio web recuerde información que cambia la forma en que se comporta o se ve.
                      </p>
                      <p className="text-xs text-gray-500">
                        Incluye preferencias de idioma y configuración regional.
                      </p>
                    </div>
                    <div className="flex items-center ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.preferences}
                        onChange={() => handlePreferenceChange('preferences')}
                        className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleAcceptAll}
                    className="flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Aceptar todas
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
                  >
                    Guardar preferencias
                  </button>
                  <button
                    onClick={() => setShowBanner(false)}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Rechazar todas
                  </button>
                </div>

                <div className="text-xs text-gray-500 text-center">
                  Para obtener más información, consulte nuestra{' '}
                  <a 
                    href="/cookies" 
                    className="text-primary-600 hover:text-primary-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    política de cookies
                  </a>{' '}
                  y nuestra{' '}
                  <a 
                    href="/privacidad" 
                    className="text-primary-600 hover:text-primary-800 underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    política de privacidad
                  </a>.
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner; 